import { useCallback, useEffect, useRef, useState } from 'react';

import { TimerStatus } from './persisted-timer.models';
import {
  getActiveEntry,
  getRemainingTime,
  removeTimerEntry,
  saveTimerEntry,
} from './persisted-timer.utils';

interface UsePersistedTimerOptions {
  phone: string | null;
  duration?: number;
  autostart?: boolean;
}

interface UsePersistedTimerReturn {
  time: number;
  start: (overridePhone?: string) => void;
  status: TimerStatus;
}

export function usePersistedTimer({
  phone,
  duration = 60,
  autostart = false,
}: UsePersistedTimerOptions): UsePersistedTimerReturn {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedPhoneRef = useRef<string | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const computeInitialState = (): { time: number; status: TimerStatus; startTime: number | null } => {
    if (phone) {
      const entry = getActiveEntry(phone, duration);
      if (entry) {
        return {
          time: getRemainingTime(entry, duration),
          status: 'RUNNING',
          startTime: entry.startTime,
        };
      }
    }
    return { time: duration, status: 'STOPPED', startTime: null };
  };

  const [initial] = useState(() => computeInitialState());
  const [time, setTime] = useState<number>(initial.time);
  const [status, setStatus] = useState<TimerStatus>(initial.status);

  const clearTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const tick = useCallback(() => {
    if (startTimeRef.current === null) return;
    const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const remaining = Math.max(duration - elapsed, 0);
    setTime(remaining);
  }, [duration]);

  const startInterval = useCallback(() => {
    clearTimer();
    intervalRef.current = setInterval(tick, 1000);
  }, [clearTimer, tick]);

  const start = useCallback((overridePhone?: string) => {
    const effectivePhone = overridePhone ?? phone;
    if (!effectivePhone) return;
    startedPhoneRef.current = effectivePhone;
    startTimeRef.current = Date.now();
    saveTimerEntry(effectivePhone);
    setTime(duration);
    setStatus('RUNNING');
    startInterval();
  }, [duration, phone, startInterval]);

  useEffect(() => {
    if (time === 0 && status === 'RUNNING') {
      clearTimer();
      setStatus('STOPPED');
      if (startedPhoneRef.current) {
        removeTimerEntry(startedPhoneRef.current);
        startedPhoneRef.current = null;
      }
      startTimeRef.current = null;
    }
  }, [time, status, clearTimer]);

  useEffect(() => {
    if (initial.status === 'RUNNING' && phone && initial.startTime !== null) {
      startedPhoneRef.current = phone;
      startTimeRef.current = initial.startTime;
      startInterval();
    } else if (autostart && phone) {
      start();
    }
    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { time, start, status };
}
